import React from 'react';
import { Info, Target, FileText, TrendingUp, CheckSquare, LineChart, Square } from 'lucide-react';
import DashboardLayout from '../../components/dashboard/DashboardLayout';

const QuantifiableResultsWorksheet = () => {
  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto px-6 py-12 text-gray-800">
        {/* Header */}
        <h1 className="text-4xl font-bold mb-8">Quantifiable Results Worksheet</h1>

        {/* Info Box */}
        <div className="bg-gray-100 p-6 rounded-lg mb-12">
          <div className="flex items-start gap-3 mb-4">
            <Info className="w-5 h-5 mt-1" />
            <div>
              <h2 className="font-medium mb-2">Essential Overview</h2>
              <p className="text-gray-700">
                Showcasing quantifiable results on your resume highlights your impact and value. 
                Use this worksheet to brainstorm and structure data-driven achievements that will 
                stand out to employers.
              </p>
            </div>
          </div>
        </div>

        {/* Key Achievements Section */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <Target className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold mb-4">1. Identify Key Achievements</h3>
              <p className="mb-4">Think about your past roles and write down significant accomplishments. Focus on areas where you:</p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Increased revenue or sales</span>
                </li>
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Reduced costs or time</span>
                </li>
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Improved processes or efficiency</span>
                </li>
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Enhanced customer satisfaction</span>
                </li>
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Achieved recognition or awards</span>
                </li>
              </ul>
              
              <div className="bg-white p-4 rounded-lg">
                <h4 className="font-medium mb-3">Example Prompts:</h4>
                <ul className="space-y-2">
                  <li>• How did I save the company money or time?</li>
                  <li>• What processes did I improve?</li>
                  <li>• Did I exceed any goals or targets?</li>
                  <li>• How did my work contribute to the organization's success?</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Quantify Results Section */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <TrendingUp className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold mb-4">2. Quantify Your Results</h3>
              <p className="mb-4">Use specific metrics to describe your achievements. Quantifiable data gives your accomplishments weight and credibility.</p>
              
              <div className="space-y-4">
                {[
                  {
                    category: "Revenue/Sales",
                    example: "Increased sales by 20% in Q2 2023, generating an additional $50,000 in revenue."
                  },
                  {
                    category: "Cost Savings",
                    example: "Implemented a new inventory system that reduced operational costs by 15%, saving $10,000 annually."
                  },
                  {
                    category: "Efficiency",
                    example: "Streamlined the customer onboarding process, reducing average onboarding time from 5 days to 2 days."
                  },
                  {
                    category: "Customer Satisfaction",
                    example: "Boosted customer satisfaction scores by 10% through improved service protocols."
                  }
                ].map((item, index) => (
                  <div key={index} className="bg-white p-4 rounded-lg">
                    <p className="font-medium text-blue-800 mb-2">{item.category}:</p>
                    <p className="text-gray-600 italic">{item.example}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Action Verbs Section */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <FileText className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold mb-4">3. Action Verbs for Quantifiable Results</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  "Increased",
                  "Reduced",
                  "Spearheaded",
                  "Optimized",
                  "Generated",
                  "Enhanced",
                  "Achieved",
                  "Implemented"
                ].map((verb, index) => (
                  <div key={index} className="bg-white p-3 rounded-lg text-center">
                    {verb}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Worksheet Table */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <LineChart className="w-5 h-5 text-blue-600" />
            <div className="w-full">
              <h3 className="font-semibold mb-4">4. Structured Worksheet</h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse bg-white rounded-lg">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border p-3 text-left">Project/Task</th>
                      <th className="border p-3 text-left">Action Taken</th>
                      <th className="border p-3 text-left">Result</th>
                      <th className="border p-3 text-left">Metric/Impact</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border p-3">Improved website SEO</td>
                      <td className="border p-3">Optimized on-page keywords and content</td>
                      <td className="border p-3">Increased organic traffic</td>
                      <td className="border p-3">By 30% in six months</td>
                    </tr>
                    <tr>
                      <td className="border p-3">Reduced processing time</td>
                      <td className="border p-3">Automated data entry process</td>
                      <td className="border p-3">Saved employee hours</td>
                      <td className="border p-3">20 hours/month</td>
                    </tr>
                    <tr>
                      <td className="border p-3">Boosted team productivity</td>
                      <td className="border p-3">Introduced a new project management tool</td>
                      <td className="border p-3">Enhanced task completion rates</td>
                      <td className="border p-3">By 25%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Final Tips */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <CheckSquare className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold mb-4">5. Final Tips</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span><strong>Be Honest:</strong> Ensure all metrics are accurate and verifiable.</span>
                </li>
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span><strong>Tailor for Each Job:</strong> Highlight the most relevant results for the position you're applying to.</span>
                </li>
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span><strong>Keep it Simple:</strong> Use clear, concise language to describe your achievements.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Closing Note */}
        <div className="bg-gray-100 p-6 rounded-lg">
          <p className="text-gray-700">
            Use this worksheet regularly to update your resume with compelling, quantifiable results 
            that set you apart from the competition!
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default QuantifiableResultsWorksheet;